import React, { useCallback, useState } from 'react';
import { Calculator, Save, RefreshCw, Download, Percent, DollarSign } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { isPositiveNumber } from '../../../../../utils/validation';
import { calculateVAT } from '../../../../../utils/calculations/finance';

/**
 * Компонент основной формы расчета НДС
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  vatRates,
  saveCalculation,
  customVatRate,
  setCustomVatRate
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'includeResults') {
      // Для чекбоксов включения результатов
      setFormData(prev => ({
        ...prev,
        includeResults: {
          ...prev.includeResults,
          [value]: checked
        }
      }));
    } else if (name === 'customVatRate') {
      // Для пользовательской ставки НДС
      setCustomVatRate(value);
    } else {
      // Для остальных полей
      const fieldValue = type === 'checkbox' ? checked : value;
      setFormData(prev => ({
        ...prev,
        [name]: fieldValue
      }));
    }
    
    // Очищаем ошибку если она есть
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData, setCustomVatRate]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const { amount, vatRate, calculationType, precision } = formData;

    // Парсинг числовых значений
    const numAmount = parseFloatOrDefault(amount, -1);
    const numPrecision = parseInt(precision, 10);

    // Валидация суммы
    if (numAmount <= 0) {
      newErrors.amount = 'Сумма должна быть положительной.';
    } else if (numAmount > 10000000000) {
      newErrors.amount = 'Максимальная сумма 10 млрд ₽.';
    }

    // Валидация ставки НДС
    if (vatRate === 'custom') {
      const numCustomVatRate = parseFloatOrDefault(customVatRate, -1);
      if (numCustomVatRate < 0) {
        newErrors.customVatRate = 'Ставка НДС не может быть отрицательной.';
      } else if (numCustomVatRate > 100) {
        newErrors.customVatRate = 'Ставка НДС не может быть больше 100%.';
      }
    } else if (vatRate !== 'none' && !vatRates.some(rate => rate.id === vatRate)) {
      newErrors.vatRate = 'Выберите корректную ставку НДС.';
    }

    // Валидация типа расчета
    if (!['add', 'extract'].includes(calculationType)) {
      newErrors.calculationType = 'Выберите корректный тип расчета.';
    }

    // Валидация точности
    if (isNaN(numPrecision) || numPrecision < 0 || numPrecision > 4) {
      newErrors.precision = 'Укажите корректное количество знаков (0-4).';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors, customVatRate, vatRates]);

  // Выполнение расчета
  const executeCalculation = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        const { amount, vatRate, calculationType, precision } = formData;
        
        // Определяем ставку НДС для расчета
        let rateForCalculation = 0;
        if (vatRate === 'custom') {
          rateForCalculation = parseFloatOrDefault(customVatRate, 0);
        } else if (vatRate === 'none') {
          rateForCalculation = 0;
        } else {
          rateForCalculation = parseFloat(vatRate);
        }

        // Сумма для расчета
        const numAmount = parseFloatOrDefault(amount, 0);
        const numPrecision = parseInt(precision, 10);
        
        // Выполняем расчет с помощью утилиты
        const calculationResult = calculateVAT(
          numAmount, 
          rateForCalculation, 
          calculationType === 'extract' // includesVat: true для выделения, false для начисления
        );

        // Округляем до заданной точности
        const roundToPrecision = (value) => {
          const factor = Math.pow(10, numPrecision);
          return Math.round(value * factor) / factor;
        };

        // Форматируем результаты
        const formattedResults = {
          amountWithVAT: roundToPrecision(calculationResult.amountWithVAT),
          amountWithoutVAT: roundToPrecision(calculationResult.amountWithoutVAT),
          vatAmount: roundToPrecision(calculationResult.vatAmount),
          vatRate: vatRate === 'custom' ? parseFloatOrDefault(customVatRate, 0) : 
                  vatRate === 'none' ? 0 : parseFloat(vatRate),
          calculationType: calculationType,
          precision: numPrecision,
          date: new Date().toISOString()
        };

        setResults(formattedResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные.` });
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, setErrors, setResults, setIsCalculating, customVatRate]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      amount: '10000',
      vatRate: '20',
      calculationType: 'add',
      precision: '2',
      includeResults: {
        withVAT: true,
        withoutVAT: true,
        vatAmount: true
      },
      description: ''
    });
    setCustomVatRate('');
    setResults(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Параметр', 'Значение'];
    
    // Определяем строки
    const rows = [
      ['Исходная сумма', formData.amount + ' ₽'],
      ['Тип расчета', formData.calculationType === 'add' ? 'Начисление НДС' : 'Выделение НДС'],
      ['Ставка НДС', formData.vatRate === 'none' ? 'Без НДС' : 
        (formData.vatRate === 'custom' ? customVatRate : formData.vatRate) + ' %'],
      ['Сумма без НДС', results.amountWithoutVAT + ' ₽'],
      ['Сумма НДС', results.vatAmount + ' ₽'],
      ['Сумма с НДС', results.amountWithVAT + ' ₽'],
      ['Дата расчета', new Date(results.date).toLocaleDateString('ru-RU') + ' ' + 
        new Date(results.date).toLocaleTimeString('ru-RU')]
    ];

    // Формируем CSV
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    
    // Создаем файл для скачивания
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_ндс.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    executeCalculation();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calculator className="w-6 h-6 text-green-500" />
          Расчет НДС
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Сумма */}
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <DollarSign className="w-4 h-4 mr-2 text-green-500" />
              Сумма
            </label>
            <input
              type="text"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.amount ? 'input-error' : ''}`}
              placeholder="Введите сумму"
            />
            {errors.amount && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.amount}
              </p>
            )}
          </div>

          {/* Тип расчета */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Тип расчета
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="calculationType"
                  value="add"
                  checked={formData.calculationType === 'add'}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Начислить НДС
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="calculationType"
                  value="extract"
                  checked={formData.calculationType === 'extract'}
                  onChange={handleInputChange}
                  className="form-radio"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Выделить НДС
                </span>
              </label>
            </div>
            {errors.calculationType && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.calculationType}
              </p>
            )}
          </div>

          {/* Ставка НДС */}
          <div>
            <label htmlFor="vatRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Percent className="w-4 h-4 mr-2 text-green-500" />
              Ставка НДС
            </label>
            <select
              id="vatRate"
              name="vatRate"
              value={formData.vatRate}
              onChange={handleInputChange}
              className={`form-select w-full ${errors.vatRate ? 'input-error' : ''}`}
            >
              {vatRates.map((rate) => (
                <option key={rate.id} value={rate.id}>
                  {rate.name} ({rate.description})
                </option>
              ))}
            </select>
            {errors.vatRate && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.vatRate}
              </p>
            )}
          </div>

          {/* Пользовательская ставка НДС (если выбрана опция "Своя ставка") */}
          {formData.vatRate === 'custom' && (
            <div>
              <label htmlFor="customVatRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Введите ставку НДС (%)
              </label>
              <input
                type="text"
                id="customVatRate"
                name="customVatRate"
                value={customVatRate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.customVatRate ? 'input-error' : ''}`}
                placeholder="Например: 18.5"
              />
              {errors.customVatRate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.customVatRate}
                </p>
              )}
            </div>
          )}

          {/* Точность расчетов */}
          <div>
            <label htmlFor="precision" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Знаков после запятой
            </label>
            <select
              id="precision"
              name="precision"
              value={formData.precision}
              onChange={handleInputChange}
              className={`form-select w-full ${errors.precision ? 'input-error' : ''}`}
            >
              <option value="0">0 (целые числа)</option>
              <option value="1">1 знак</option>
              <option value="2">2 знака</option>
              <option value="3">3 знака</option>
              <option value="4">4 знака</option>
            </select>
            {errors.precision && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.precision}
              </p>
            )}
          </div>
        </div>

        {/* Включаемые результаты */}
        <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Отображаемые результаты
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeResults"
                value="withVAT"
                checked={formData.includeResults.withVAT}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Сумма с НДС
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeResults"
                value="withoutVAT"
                checked={formData.includeResults.withoutVAT}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Сумма без НДС
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeResults"
                value="vatAmount"
                checked={formData.includeResults.vatAmount}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Сумма НДС
              </span>
            </label>
          </div>
        </div>

        {/* Краткое описание расчета */}
        <div className="mb-6">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Описание расчета (необязательно)
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="form-textarea w-full"
            placeholder="Добавьте описание для сохраненного расчета"
            rows="2"
          />
        </div>

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Вычисляем...' : 'Рассчитать НДС'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Экспорт (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;